/**
 * Abre a janela de impressão do navegador assim que uma folha de impressão
 * monta, e avisa quando ela fecha.
 *
 * Papel: aqui ficam a espera pelas imagens da folha e o efeito que chama
 * `window.print()` e escuta o `afterprint` — o mesmo para toda folha que o
 * navegador salva como PDF.
 *
 * Como usar: a folha chama `useImpressaoAoMontar` com o `onConcluida` que
 * recebeu e põe a `ref` devolvida no elemento de fora dela. A impressão
 * acontece uma vez por montagem, e por isso quem monta a folha deverá dar a
 * ela uma `key` nova a cada pedido de impressão: sem a `key`, um segundo
 * pedido feito enquanto a folha ainda está montada — o navegador que não
 * dispara `afterprint`, e nunca a deixa desmontar — não imprime nada, e a
 * página continua escondida da impressão atrás da folha.
 *
 * Com `limiteEmMs`, `onConcluida` é chamado também quando o `afterprint` não
 * chega até esse tempo depois de `window.print()` voltar — o que vier
 * primeiro, uma vez só. É o que devolve a página ao estado de antes no
 * navegador que não dispara o evento.
 *
 * A janela só abre depois que todas as `<img>` da folha terminam de carregar,
 * e a imagem que falha fica de fora sem travar a impressão. Desmontada antes
 * disso — no `StrictMode`, que monta duas vezes, é a primeira montagem —, a
 * folha não chega a imprimir, e o `afterprint` que ela escutava é retirado.
 */
import { useEffect, useEffectEvent, useRef } from 'react';

/**
 * Espera as imagens da folha terminarem de carregar.
 *
 * @param folha - O elemento da folha, ou `null` antes de ela montar.
 * @returns Uma promessa que sempre cumpre, com ou sem as imagens.
 */
async function esperarAsImagens(folha: HTMLElement | null): Promise<void> {
  if (folha === null) return;
  await Promise.all(
    Array.from(folha.querySelectorAll('img'), async (imagem) => {
      try {
        await imagem.decode();
      } catch {
        // A imagem que falhou fica de fora; a folha sai assim mesmo.
      }
    }),
  );
}

/**
 * Imprime a folha ao montar e chama `onConcluida` quando a janela fecha.
 *
 * Quem chama é a própria folha de impressão, uma vez, no corpo do componente.
 * `onConcluida` é lido no instante do `afterprint`, e não no da montagem, então
 * trocar a função entre um render e outro não reabre a impressão.
 *
 * @param onConcluida - Chamado uma vez, quando o navegador avisa que a janela
 *   de impressão fechou, impressa ou cancelada; é aí que quem montou a folha
 *   deverá desmontá-la.
 * @param limiteEmMs - Quanto esperar pelo `afterprint` depois que
 *   `window.print()` volta; omitido, espera sem limite.
 * @returns A `ref` a pôr no elemento de fora da folha, que é onde as imagens
 *   são procuradas.
 */
export function useImpressaoAoMontar<T extends HTMLElement>(
  onConcluida: () => void,
  limiteEmMs?: number,
) {
  const refDaFolha = useRef<T>(null);
  const concluir = useEffectEvent(onConcluida);

  useEffect(() => {
    let desmontada = false;
    let concluida = false;
    let temporizador: number | null = null;

    /** Avisa quem montou a folha que a janela de impressão fechou. */
    function handleDepoisDeImprimir() {
      if (concluida) return;
      concluida = true;
      if (temporizador !== null) window.clearTimeout(temporizador);
      window.removeEventListener('afterprint', handleDepoisDeImprimir);
      concluir();
    }

    async function imprimir() {
      await esperarAsImagens(refDaFolha.current);
      if (desmontada) return;
      window.addEventListener('afterprint', handleDepoisDeImprimir, { once: true });
      window.print();
      if (limiteEmMs !== undefined && !desmontada && !concluida) {
        temporizador = window.setTimeout(handleDepoisDeImprimir, limiteEmMs);
      }
    }

    void imprimir();

    return () => {
      desmontada = true;
      if (temporizador !== null) window.clearTimeout(temporizador);
      window.removeEventListener('afterprint', handleDepoisDeImprimir);
    };
    // Quem passa `limiteEmMs` passa uma constante; trocá-lo entre renders
    // imprimiria de novo.
  }, [limiteEmMs]);

  return refDaFolha;
}
